import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Switch,
  useMediaQuery,
} from 'ui/mui';
import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  ArrowBack as BackIcon,
  DeleteOutline as DeleteIcon,
} from 'ui/icons';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth, getStoredUser } from '../utils/auth';
import { STAFF_PERMISSION_DEFINITIONS, normalizePermissions } from '../utils/staffPermissions';
import './AdminPanel.css';

function formatLastLogin(value) {
  if (!value) return 'Never';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function initialsFromName(name, email) {
  const base = String(name || email || '').trim();
  if (!base) return '?';

  return base
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase() || '?';
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:767px)');
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKeys, setSavingKeys] = useState({});
  const [removingEmail, setRemovingEmail] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const currentUser = useMemo(() => getStoredUser(), []);

  useEffect(() => {
    let mounted = true;

    async function loadStaff() {
      setLoading(true);

      try {
        const response = await fetchWithAuth('/api/admin/permissions');
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            localStorage.removeItem('user');
            navigate('/login');
            return;
          }
          throw new Error(payload?.details || payload?.message || `HTTP ${response.status}`);
        }

        if (!mounted) return;

        setStaff(
          (Array.isArray(payload?.staff) ? payload.staff : []).map((entry) => ({
            ...entry,
            permissions: normalizePermissions(entry?.permissions),
          }))
        );
      } catch (error) {
        if (!mounted) return;

        const message = error.message || 'Failed to load staff permissions';
        setSnackbar({
          open: true,
          severity: 'error',
          message,
        });

        if (/HTTP 401|HTTP 403/i.test(message)) {
          localStorage.removeItem('user');
          navigate('/login');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadStaff();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  const setSavingState = (key, value) => {
    setSavingKeys((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleTogglePermission = async (staffMember, permissionKey, checked) => {
    const email = String(staffMember?.email || '').toLowerCase();
    if (!email) return;

    const requestKey = `${email}:${permissionKey}`;
    const previousPermissions = normalizePermissions(staffMember.permissions);
    const nextPermissions = {
      ...previousPermissions,
      [permissionKey]: checked,
    };

    setStaff((prev) =>
      prev.map((entry) =>
        entry.email === email
          ? { ...entry, permissions: nextPermissions }
          : entry
      )
    );
    setSavingState(requestKey, true);

    try {
      const response = await fetchWithAuth('/api/admin/permissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          permissions: nextPermissions,
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.details || payload?.message || `HTTP ${response.status}`);
      }

      setStaff((prev) =>
        prev.map((entry) =>
          entry.email === email
            ? {
                ...entry,
                ...payload.staff,
                permissions: normalizePermissions(payload?.staff?.permissions || nextPermissions),
              }
            : entry
        )
      );

      setSnackbar({
        open: true,
        severity: 'success',
        message: `Saved ${staffMember.name || email}.`,
      });
    } catch (error) {
      setStaff((prev) =>
        prev.map((entry) =>
          entry.email === email
            ? { ...entry, permissions: previousPermissions }
            : entry
        )
      );
      setSnackbar({
        open: true,
        severity: 'error',
        message: error.message || 'Failed to save permissions',
      });
    } finally {
      setSavingState(requestKey, false);
    }
  };

  const handleRemove = async (staffMember) => {
    const email = String(staffMember?.email || '').toLowerCase();
    if (!email) return;

    const confirmed = window.confirm(`Remove ${email} from staff permissions?`);
    if (!confirmed) return;

    setRemovingEmail(email);

    try {
      const response = await fetchWithAuth('/api/admin/permissions', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.details || payload?.message || `HTTP ${response.status}`);
      }

      setStaff((prev) => prev.filter((entry) => entry.email !== email));
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Removed ${email}.`,
      });
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error.message || 'Failed to remove staff member',
      });
    } finally {
      setRemovingEmail('');
    }
  };

  const renderPermissionToggle = (staffMember, permission) => {
    const requestKey = `${staffMember.email}:${permission.key}`;
    const checked = Boolean(staffMember.permissions?.[permission.key]);
    const disabled = Boolean(savingKeys[requestKey]);

    return (
      <div key={permission.key} className="adm-perm">
        <p className="adm-perm__label">
          {permission.label}
        </p>
        <Switch
          checked={checked}
          disabled={disabled}
          onChange={(event) => handleTogglePermission(staffMember, permission.key, event.target.checked)}
          color="primary"
        />
      </div>
    );
  };

  const renderPermissionGrid = (staffMember) => (
    <div className={isMobile ? 'adm-perms adm-perms--single' : 'adm-perms'}>
      {STAFF_PERMISSION_DEFINITIONS.map((permission) => renderPermissionToggle(staffMember, permission))}
    </div>
  );

  if (loading) {
    return (
      <div className="rc-page adm-page">
        <div className="adm-loading">
          <CircularProgress color="primary" />
          <p className="adm-loading__text">
            Loading staff permissions...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rc-page adm-page">
      <header className="adm-header">
        <IconButton
          onClick={() => navigate('/dashboard')}
          className="adm-back"
        >
          <BackIcon />
        </IconButton>
        <div className="adm-header__text">
          <p className="rc-eyebrow">Admin</p>
          <div className="adm-header__title">
            <span className="adm-mark"><AdminPanelSettingsIcon /></span>
            <h1 className="rc-page-title">
              Staff Permissions
            </h1>
          </div>
          <p className="rc-page-description">
            Manage portal access for each staff account.
          </p>
        </div>
      </header>

      {isMobile ? (
        <div className="adm-list">
          {staff.map((staffMember) => (
            <div
              key={staffMember.id || staffMember.email}
              className="adm-card"
            >
              <div className="adm-person adm-person--top">
                <Avatar
                  src={staffMember.picture || undefined}
                  alt={staffMember.name || staffMember.email}
                >
                  {initialsFromName(staffMember.name, staffMember.email)}
                </Avatar>
                <div className="adm-person__text">
                  <p className="adm-person__name">
                    {staffMember.name || 'Unknown'}
                  </p>
                  <p className="adm-person__email">
                    {staffMember.email}
                  </p>
                  {String(currentUser?.email || '').toLowerCase() === String(staffMember.email || '').toLowerCase() && (
                    <p className="adm-person__current">
                      Current session
                    </p>
                  )}
                  <p className="adm-person__meta">
                    Last login: {formatLastLogin(staffMember.last_login)}
                  </p>
                </div>
              </div>

              <div>
                {renderPermissionGrid(staffMember)}
              </div>

              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={removingEmail === staffMember.email}
                onClick={() => handleRemove(staffMember)}
              >
                Remove
              </Button>
            </div>
          ))}
          {staff.length === 0 && (
            <div className="adm-empty">
              <div>No staff records found.</div>
            </div>
          )}
        </div>
      ) : (
        <div className="adm-list">
          {staff.length === 0 ? (
            <div className="adm-empty">
              <p>No staff records found.</p>
            </div>
          ) : staff.map((staffMember) => (
            <div
              key={staffMember.id || staffMember.email}
              className="adm-card"
            >
              {/* Staff info row */}
              <div className="adm-person">
                <Avatar
                  src={staffMember.picture || undefined}
                  alt={staffMember.name || staffMember.email}
                >
                  {initialsFromName(staffMember.name, staffMember.email)}
                </Avatar>
                <div className="adm-person__text">
                  <p className="adm-person__name">
                    {staffMember.name || 'Unknown'}
                    {String(currentUser?.email || '').toLowerCase() === String(staffMember.email || '').toLowerCase() && (
                      <span className="adm-you">You</span>
                    )}
                  </p>
                  <p className="adm-person__email">
                    {staffMember.email}
                  </p>
                  <p className="adm-person__meta">
                    {formatLastLogin(staffMember.last_login)}
                  </p>
                </div>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={removingEmail === staffMember.email}
                  onClick={() => handleRemove(staffMember)}
                  size="small"
                >
                  Remove
                </Button>
              </div>
              {/* Permissions grid */}
              {renderPermissionGrid(staffMember)}
            </div>
          ))}
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
